import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base, toast } from '@itsrever/design-system'
import { LoadError } from '@/ui/shared'
import {
  appAutomationTokenStatus,
  expiryOfDay
} from '@/domain/nango-connection'
import {
  useSaveShopifyAppAutomationToken,
  useShopifyAppAutomationToken
} from '@/application/manage-nango-connections'
import { RAW_TEXT } from './hub-parts'

const {
  Button,
  Card,
  DialogRoot,
  DialogPortal,
  DialogBackdrop,
  DialogPopup,
  DialogTitle,
  DialogDescription,
  DialogCloseButton,
  DialogHeader,
  DialogFooter,
  FieldRoot,
  FieldLabel,
  FieldDescription,
  Input
} = Base

const DAY_MS = 24 * 60 * 60 * 1000

// The token REVER deploys its Shopify app to this store with, for WFP and size suggestions.
export function AppAutomationTokenCard({
  ecommerceId,
  readOnly = false
}: {
  ecommerceId: string
  readOnly?: boolean
}) {
  const { t, i18n } = useTranslation()
  const copy = (key: string) => t(`integrations_hub.app_token.${key}`)
  const [editing, setEditing] = useState(false)
  const { token, loadingToken, tokenError, reloadToken } =
    useShopifyAppAutomationToken({ enabled: true, ecommerceId })

  if (loadingToken) return null
  if (tokenError) {
    return (
      <LoadError message={copy('load_error')} onRetry={() => reloadToken()} />
    )
  }
  // A store REVER deploys nothing to needs no token, but one it has stays replaceable.
  if (!token || (!token.required && !token.tokenSet)) return null

  const status = appAutomationTokenStatus(token, new Date())
  // Saved as the start of a day in UTC: shown in UTC, so it reads as the day that was entered.
  const date = token.expiresAt?.toLocaleDateString(i18n.language, {
    timeZone: 'UTC'
  })

  return (
    <Card variant="flat" className="flex items-center gap-4 px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-medium text-neutral-900">{copy('title')}</p>
        <p className="text-xs text-neutral-600">{copy('description')}</p>
        <p
          className={
            status === 'expires'
              ? 'text-xs text-neutral-600'
              : 'text-xs text-red-700'
          }
        >
          {t(`integrations_hub.app_token.${status}`, { date, ...RAW_TEXT })}
        </p>
      </div>
      {readOnly ? null : (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setEditing(true)}
        >
          {copy(token.tokenSet ? 'replace' : 'add')}
        </Button>
      )}
      {editing ? (
        <AppAutomationTokenDialog
          ecommerceId={ecommerceId}
          onClose={() => setEditing(false)}
        />
      ) : null}
    </Card>
  )
}

function AppAutomationTokenDialog({
  ecommerceId,
  onClose
}: {
  ecommerceId: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const copy = (key: string) => t(`integrations_hub.app_token.${key}`)
  const [token, setToken] = useState('')
  const [day, setDay] = useState('')
  const { saveToken, savingToken } = useSaveShopifyAppAutomationToken()
  const expiresAt = expiryOfDay(day)
  const canSave =
    token.trim() !== '' && !!expiresAt && expiresAt.getTime() > Date.now()
  const tomorrow = new Date(Date.now() + DAY_MS).toISOString().slice(0, 10)

  const save = async () => {
    if (!expiresAt) return
    try {
      await saveToken({ ecommerceId, token: token.trim(), expiresAt })
      toast({ text: copy('saved'), variant: 'success' })
      onClose()
    } catch {
      toast({ text: copy('save_error'), variant: 'error' })
    }
  }

  return (
    <DialogRoot
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogPortal>
        <DialogBackdrop />
        <DialogPopup size="md">
          <DialogCloseButton />
          <div className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>
                {t('integrations_hub.app_token.dialog_title', {
                  store: ecommerceId,
                  ...RAW_TEXT
                })}
              </DialogTitle>
              <DialogDescription>
                {copy('dialog_description')}
              </DialogDescription>
            </DialogHeader>
            <FieldRoot>
              <FieldLabel>{copy('token_label')}</FieldLabel>
              <Input
                type="password"
                autoComplete="off"
                value={token}
                onChange={(e) => setToken(e.currentTarget.value)}
              />
            </FieldRoot>
            <FieldRoot>
              <FieldLabel>{copy('expires_label')}</FieldLabel>
              <Input
                type="date"
                min={tomorrow}
                value={day}
                onChange={(e) => setDay(e.currentTarget.value)}
              />
              <FieldDescription>{copy('expires_help')}</FieldDescription>
            </FieldRoot>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={onClose}>
                {copy('cancel')}
              </Button>
              <Button
                type="button"
                variant="primary"
                disabled={!canSave}
                loading={savingToken}
                onClick={save}
              >
                {copy('save')}
              </Button>
            </DialogFooter>
          </div>
        </DialogPopup>
      </DialogPortal>
    </DialogRoot>
  )
}
